import React, { useState, useEffect, useRef } from 'react';
import { FaMapMarkerAlt, FaSearch, FaTimes } from 'react-icons/fa';
import { debounce } from '../utils/helpers';
import GoogleMapsService from '../services/googleMapsService';
import './LocationSuggestions.css';

const LocationSuggestions = ({ 
  value, 
  onChange, 
  onLocationSelect, 
  placeholder, 
  error, 
  name 
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Debounced search function
  const debouncedSearch = debounce(async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const results = await searchLocations(query);
      setSuggestions(results);
    } catch (error) {
      console.error('Error searching locations:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, 300);

  // Geocode address using OpenRouteService
  const geocodeWithOpenRoute = async (address) => {
    try {
      const response = await fetch(
        `https://api.openrouteservice.org/geocode/search?api_key=${process.env.REACT_APP_OPENROUTE_API_KEY || '5b3ce3597851110001cf6248a8b8c8b8b8b8b8b8'}&text=${encodeURIComponent(address)}&size=1`
      );

      if (!response.ok) {
        throw new Error('OpenRouteService geocoding failed');
      }

      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const coords = data.features[0].geometry.coordinates;
        return {
          lat: coords[1], // OpenRouteService returns [lng, lat]
          lng: coords[0]
        };
      }

      return null;
    } catch (error) {
      console.error('OpenRouteService geocoding error:', error);
      return null;
    }
  };

  // Search locations using Google Maps API (primary) or OpenRouteService (fallback)
  const searchLocations = async (query) => {
    try {
      // Try Google Maps API first if configured
      if (GoogleMapsService.isConfigured()) {
        console.log('Using Google Maps API for location search');
        return await GoogleMapsService.getLocationSuggestions(query);
      }

      // Fallback to OpenRouteService API
      console.log('Using OpenRouteService API as fallback');
      const response = await fetch(
        `https://api.openrouteservice.org/geocode/search?api_key=${process.env.REACT_APP_OPENROUTE_API_KEY || '5b3ce3597851110001cf6248a8b8c8b8b8b8b8b8'}&text=${encodeURIComponent(query)}&size=5`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch locations');
      }

      const data = await response.json();

      if (data.features && data.features.length > 0) {
        return data.features.map(feature => ({
          id: feature.properties.id,
          name: feature.properties.label,
          coords: {
            lat: feature.geometry.coordinates[1],
            lng: feature.geometry.coordinates[0]
          },
          address: feature.properties.label
        }));
      }

      return [];
    } catch (error) {
      console.error('Location search API error:', error);

      // Fallback to mock data for demo purposes
      console.log('Using mock data as final fallback');
      return getMockLocations(query);
    }
  };

  // Comprehensive location database for Tamil Nadu and neighboring states
  const getMockLocations = (query) => {
    const mockLocations = [
      // Chennai - Major Areas and Landmarks
      { id: '1', name: 'Chennai International Airport', coords: { lat: 12.9941, lng: 80.1709 }, address: 'Tirusulam, Chennai, Tamil Nadu' },
      { id: '2', name: 'Chennai Central Railway Station', coords: { lat: 13.0827, lng: 80.2707 }, address: 'Park Town, Chennai, Tamil Nadu' },
      { id: '3', name: 'Chennai Egmore Railway Station', coords: { lat: 13.0827, lng: 80.2707 }, address: 'Egmore, Chennai, Tamil Nadu' },
      
      // Chennai Areas
      { id: '4', name: 'T. Nagar', coords: { lat: 13.0418, lng: 80.2341 }, address: 'T. Nagar, Chennai, Tamil Nadu' },
      { id: '5', name: 'Anna Nagar', coords: { lat: 13.0827, lng: 80.2707 }, address: 'Anna Nagar, Chennai, Tamil Nadu' },
      { id: '6', name: 'Adyar', coords: { lat: 13.0067, lng: 80.2206 }, address: 'Adyar, Chennai, Tamil Nadu' },
      { id: '7', name: 'Velachery', coords: { lat: 12.9816, lng: 80.2206 }, address: 'Velachery, Chennai, Tamil Nadu' },
      { id: '8', name: 'Tambaram', coords: { lat: 12.9246, lng: 80.1273 }, address: 'Tambaram, Chennai, Tamil Nadu' },
      { id: '9', name: 'OMR (Old Mahabalipuram Road)', coords: { lat: 12.8996, lng: 80.2209 }, address: 'OMR, Chennai, Tamil Nadu' },
      { id: '10', name: 'ECR (East Coast Road)', coords: { lat: 12.8996, lng: 80.2209 }, address: 'ECR, Chennai, Tamil Nadu' },
      { id: '11', name: 'Guindy', coords: { lat: 13.0067, lng: 80.2206 }, address: 'Guindy, Chennai, Tamil Nadu' },
      { id: '12', name: 'Mylapore', coords: { lat: 13.0339, lng: 80.2620 }, address: 'Mylapore, Chennai, Tamil Nadu' },
      { id: '13', name: 'Nungambakkam', coords: { lat: 13.0604, lng: 80.2341 }, address: 'Nungambakkam, Chennai, Tamil Nadu' },
      { id: '14', name: 'Kodambakkam', coords: { lat: 13.0504, lng: 80.2206 }, address: 'Kodambakkam, Chennai, Tamil Nadu' },
      { id: '15', name: 'Pallavaram', coords: { lat: 12.9679, lng: 80.1500 }, address: 'Pallavaram, Chennai, Tamil Nadu' },
      { id: '16', name: 'Chromepet', coords: { lat: 12.9500, lng: 80.1400 }, address: 'Chromepet, Chennai, Tamil Nadu' },
      { id: '17', name: 'Medavakkam', coords: { lat: 12.9167, lng: 80.1833 }, address: 'Medavakkam, Chennai, Tamil Nadu' },
      { id: '18', name: 'Sholinganallur', coords: { lat: 12.9000, lng: 80.2167 }, address: 'Sholinganallur, Chennai, Tamil Nadu' },
      { id: '19', name: 'Thoraipakkam', coords: { lat: 12.9167, lng: 80.2333 }, address: 'Thoraipakkam, Chennai, Tamil Nadu' },
      { id: '20', name: 'Perungudi', coords: { lat: 12.9333, lng: 80.2333 }, address: 'Perungudi, Chennai, Tamil Nadu' },
      
      // Tamil Nadu Districts - Major Cities
      { id: '21', name: 'Coimbatore', coords: { lat: 11.0168, lng: 76.9558 }, address: 'Coimbatore, Tamil Nadu' },
      { id: '22', name: 'Madurai', coords: { lat: 9.9252, lng: 78.1198 }, address: 'Madurai, Tamil Nadu' },
      { id: '23', name: 'Tiruchirapalli (Trichy)', coords: { lat: 10.7905, lng: 78.7047 }, address: 'Tiruchirapalli, Tamil Nadu' },
      { id: '24', name: 'Salem', coords: { lat: 11.6643, lng: 78.1460 }, address: 'Salem, Tamil Nadu' },
      { id: '25', name: 'Tirunelveli', coords: { lat: 8.7139, lng: 77.7567 }, address: 'Tirunelveli, Tamil Nadu' },
      { id: '26', name: 'Erode', coords: { lat: 11.3410, lng: 77.7172 }, address: 'Erode, Tamil Nadu' },
      { id: '27', name: 'Vellore', coords: { lat: 12.9202, lng: 79.1500 }, address: 'Vellore, Tamil Nadu' },
      { id: '28', name: 'Thanjavur', coords: { lat: 10.7905, lng: 79.1378 }, address: 'Thanjavur, Tamil Nadu' },
      { id: '29', name: 'Kanchipuram', coords: { lat: 12.8338, lng: 79.7000 }, address: 'Kanchipuram, Tamil Nadu' },
      { id: '30', name: 'Tiruppur', coords: { lat: 11.1085, lng: 77.3411 }, address: 'Tiruppur, Tamil Nadu' },
      { id: '31', name: 'Karaikudi', coords: { lat: 10.0661, lng: 78.7678 }, address: 'Karaikudi, Tamil Nadu' },
      { id: '32', name: 'Dindigul', coords: { lat: 10.3500, lng: 77.9500 }, address: 'Dindigul, Tamil Nadu' },
      { id: '33', name: 'Tiruvannamalai', coords: { lat: 12.2300, lng: 79.0700 }, address: 'Tiruvannamalai, Tamil Nadu' },
      { id: '34', name: 'Cuddalore', coords: { lat: 11.7500, lng: 79.7500 }, address: 'Cuddalore, Tamil Nadu' },
      { id: '35', name: 'Villupuram', coords: { lat: 11.9300, lng: 79.4800 }, address: 'Villupuram, Tamil Nadu' },
      { id: '36', name: 'Krishnagiri', coords: { lat: 12.5200, lng: 78.2200 }, address: 'Krishnagiri, Tamil Nadu' },
      { id: '37', name: 'Dharmapuri', coords: { lat: 12.1200, lng: 78.1600 }, address: 'Dharmapuri, Tamil Nadu' },
      { id: '38', name: 'Namakkal', coords: { lat: 11.2200, lng: 78.1600 }, address: 'Namakkal, Tamil Nadu' },
      { id: '39', name: 'Karur', coords: { lat: 10.9500, lng: 78.0800 }, address: 'Karur, Tamil Nadu' },
      { id: '40', name: 'Perambalur', coords: { lat: 11.2300, lng: 78.8800 }, address: 'Perambalur, Tamil Nadu' },
      { id: '41', name: 'Ariyalur', coords: { lat: 11.1300, lng: 79.0800 }, address: 'Ariyalur, Tamil Nadu' },
      { id: '42', name: 'Pudukkottai', coords: { lat: 10.3800, lng: 78.8200 }, address: 'Pudukkottai, Tamil Nadu' },
      { id: '43', name: 'Sivaganga', coords: { lat: 9.8500, lng: 78.4800 }, address: 'Sivaganga, Tamil Nadu' },
      { id: '44', name: 'Ramanathapuram', coords: { lat: 9.3800, lng: 78.8200 }, address: 'Ramanathapuram, Tamil Nadu' },
      { id: '45', name: 'Virudhunagar', coords: { lat: 9.5800, lng: 77.9500 }, address: 'Virudhunagar, Tamil Nadu' },
      { id: '46', name: 'Theni', coords: { lat: 10.0100, lng: 77.4800 }, address: 'Theni, Tamil Nadu' },
      { id: '47', name: 'Sivakasi', coords: { lat: 9.4500, lng: 77.8200 }, address: 'Sivakasi, Tamil Nadu' },
      { id: '48', name: 'Tuticorin (Thoothukudi)', coords: { lat: 8.8000, lng: 78.1300 }, address: 'Tuticorin, Tamil Nadu' },
      { id: '49', name: 'Kanyakumari', coords: { lat: 8.0800, lng: 77.5700 }, address: 'Kanyakumari, Tamil Nadu' },
      { id: '50', name: 'Nagercoil', coords: { lat: 8.1800, lng: 77.4300 }, address: 'Nagercoil, Tamil Nadu' },
      
      // Tamil Nadu - Hill Stations and Tourist Places
      { id: '51', name: 'Ooty (Udhagamandalam)', coords: { lat: 11.4102, lng: 76.6952 }, address: 'Ooty, Tamil Nadu' },
      { id: '52', name: 'Kodaikanal', coords: { lat: 10.2381, lng: 77.4892 }, address: 'Kodaikanal, Tamil Nadu' },
      { id: '53', name: 'Yercaud', coords: { lat: 11.7800, lng: 78.2100 }, address: 'Yercaud, Tamil Nadu' },
      { id: '54', name: 'Kolli Hills', coords: { lat: 11.3300, lng: 78.3300 }, address: 'Kolli Hills, Tamil Nadu' },
      { id: '55', name: 'Valparai', coords: { lat: 10.3300, lng: 76.9600 }, address: 'Valparai, Tamil Nadu' },
      { id: '56', name: 'Mahabalipuram', coords: { lat: 12.6208, lng: 80.1941 }, address: 'Mahabalipuram, Tamil Nadu' },
      { id: '57', name: 'Rameswaram', coords: { lat: 9.2800, lng: 79.3000 }, address: 'Rameswaram, Tamil Nadu' },
      { id: '58', name: 'Chidambaram', coords: { lat: 11.4000, lng: 79.7000 }, address: 'Chidambaram, Tamil Nadu' },
      { id: '59', name: 'Srirangam', coords: { lat: 10.8600, lng: 78.6800 }, address: 'Srirangam, Tamil Nadu' },

      // Pondicherry (Puducherry)
      { id: '60', name: 'Pondicherry', coords: { lat: 11.9139, lng: 79.8145 }, address: 'Pondicherry' },
      { id: '61', name: 'Karaikal', coords: { lat: 10.9200, lng: 79.8300 }, address: 'Karaikal, Pondicherry' },
      { id: '62', name: 'Mahe', coords: { lat: 11.7000, lng: 75.5300 }, address: 'Mahe, Pondicherry' },
      { id: '63', name: 'Yanam', coords: { lat: 16.7300, lng: 82.2200 }, address: 'Yanam, Pondicherry' },

      // Karnataka - Major Cities
      { id: '64', name: 'Bangalore', coords: { lat: 12.9716, lng: 77.5946 }, address: 'Bangalore, Karnataka' },
      { id: '65', name: 'Mysore', coords: { lat: 12.2958, lng: 76.6394 }, address: 'Mysore, Karnataka' },
      { id: '66', name: 'Mangalore', coords: { lat: 12.9141, lng: 74.8560 }, address: 'Mangalore, Karnataka' },
      { id: '67', name: 'Hubli', coords: { lat: 15.3647, lng: 75.1240 }, address: 'Hubli, Karnataka' },
      { id: '68', name: 'Belgaum', coords: { lat: 15.8497, lng: 74.4977 }, address: 'Belgaum, Karnataka' },
      { id: '69', name: 'Gulbarga', coords: { lat: 17.3297, lng: 76.8343 }, address: 'Gulbarga, Karnataka' },
      { id: '70', name: 'Davanagere', coords: { lat: 14.4667, lng: 75.9167 }, address: 'Davanagere, Karnataka' },
      { id: '71', name: 'Bellary', coords: { lat: 15.1500, lng: 76.9333 }, address: 'Bellary, Karnataka' },
      { id: '72', name: 'Bijapur', coords: { lat: 16.8244, lng: 75.7154 }, address: 'Bijapur, Karnataka' },
      { id: '73', name: 'Shimoga', coords: { lat: 13.9300, lng: 75.5600 }, address: 'Shimoga, Karnataka' },
      { id: '74', name: 'Tumkur', coords: { lat: 13.3400, lng: 77.1000 }, address: 'Tumkur, Karnataka' },
      { id: '75', name: 'Raichur', coords: { lat: 16.2000, lng: 77.3500 }, address: 'Raichur, Karnataka' },
      { id: '76', name: 'Bidar', coords: { lat: 17.9100, lng: 77.5500 }, address: 'Bidar, Karnataka' },
      { id: '77', name: 'Hospet', coords: { lat: 15.2700, lng: 76.3900 }, address: 'Hospet, Karnataka' },
      { id: '78', name: 'Gadag', coords: { lat: 15.4300, lng: 75.6300 }, address: 'Gadag, Karnataka' },
      { id: '79', name: 'Hassan', coords: { lat: 13.0000, lng: 76.1000 }, address: 'Hassan, Karnataka' },

      // Karnataka - Hill Stations and Tourist Places
      { id: '80', name: 'Coorg (Kodagu)', coords: { lat: 12.4200, lng: 75.7400 }, address: 'Coorg, Karnataka' },
      { id: '81', name: 'Chikmagalur', coords: { lat: 13.3200, lng: 75.7700 }, address: 'Chikmagalur, Karnataka' },
      { id: '82', name: 'Hampi', coords: { lat: 15.3350, lng: 76.4600 }, address: 'Hampi, Karnataka' },
      { id: '83', name: 'Mysore Palace', coords: { lat: 12.3051, lng: 76.6552 }, address: 'Mysore Palace, Karnataka' },
      { id: '84', name: 'Jog Falls', coords: { lat: 14.2200, lng: 74.8100 }, address: 'Jog Falls, Karnataka' },

      // Andhra Pradesh - Major Cities
      { id: '85', name: 'Hyderabad', coords: { lat: 17.3850, lng: 78.4867 }, address: 'Hyderabad, Andhra Pradesh' },
      { id: '86', name: 'Vijayawada', coords: { lat: 16.5062, lng: 80.6480 }, address: 'Vijayawada, Andhra Pradesh' },
      { id: '87', name: 'Visakhapatnam', coords: { lat: 17.6868, lng: 83.2185 }, address: 'Visakhapatnam, Andhra Pradesh' },
      { id: '88', name: 'Guntur', coords: { lat: 16.3000, lng: 80.4500 }, address: 'Guntur, Andhra Pradesh' },
      { id: '89', name: 'Nellore', coords: { lat: 14.4500, lng: 79.9800 }, address: 'Nellore, Andhra Pradesh' },
      { id: '90', name: 'Kurnool', coords: { lat: 15.8300, lng: 78.0500 }, address: 'Kurnool, Andhra Pradesh' },
      { id: '91', name: 'Anantapur', coords: { lat: 14.6800, lng: 77.6000 }, address: 'Anantapur, Andhra Pradesh' },
      { id: '92', name: 'Kadapa', coords: { lat: 14.4700, lng: 78.8200 }, address: 'Kadapa, Andhra Pradesh' },
      { id: '93', name: 'Tirupati', coords: { lat: 13.6500, lng: 79.4200 }, address: 'Tirupati, Andhra Pradesh' },
      { id: '94', name: 'Chittoor', coords: { lat: 13.2000, lng: 79.1000 }, address: 'Chittoor, Andhra Pradesh' },
      { id: '95', name: 'Ongole', coords: { lat: 15.5000, lng: 80.0500 }, address: 'Ongole, Andhra Pradesh' },
      { id: '96', name: 'Eluru', coords: { lat: 16.7000, lng: 81.1000 }, address: 'Eluru, Andhra Pradesh' },
      { id: '97', name: 'Rajahmundry', coords: { lat: 17.0000, lng: 81.8000 }, address: 'Rajahmundry, Andhra Pradesh' },
      { id: '98', name: 'Kakinada', coords: { lat: 16.9600, lng: 82.2400 }, address: 'Kakinada, Andhra Pradesh' },
      { id: '99', name: 'Warangal', coords: { lat: 17.9700, lng: 79.6000 }, address: 'Warangal, Andhra Pradesh' },

      // Kerala - Major Cities
      { id: '100', name: 'Thiruvananthapuram', coords: { lat: 8.5241, lng: 76.9366 }, address: 'Thiruvananthapuram, Kerala' },
      { id: '101', name: 'Kochi (Cochin)', coords: { lat: 9.9312, lng: 76.2673 }, address: 'Kochi, Kerala' },
      { id: '102', name: 'Kozhikode (Calicut)', coords: { lat: 11.2588, lng: 75.7804 }, address: 'Kozhikode, Kerala' },
      { id: '103', name: 'Thrissur', coords: { lat: 10.5276, lng: 76.2144 }, address: 'Thrissur, Kerala' },
      { id: '104', name: 'Kollam', coords: { lat: 8.8932, lng: 76.6141 }, address: 'Kollam, Kerala' },
      { id: '105', name: 'Palakkad', coords: { lat: 10.7867, lng: 76.6548 }, address: 'Palakkad, Kerala' },
      { id: '106', name: 'Alappuzha (Alleppey)', coords: { lat: 9.4981, lng: 76.3388 }, address: 'Alappuzha, Kerala' },
      { id: '107', name: 'Kannur', coords: { lat: 11.8745, lng: 75.3704 }, address: 'Kannur, Kerala' },
      { id: '108', name: 'Kasaragod', coords: { lat: 12.5000, lng: 75.0000 }, address: 'Kasaragod, Kerala' },
      { id: '109', name: 'Malappuram', coords: { lat: 11.0500, lng: 76.0800 }, address: 'Malappuram, Kerala' },
      { id: '110', name: 'Wayanad', coords: { lat: 11.6850, lng: 76.1320 }, address: 'Wayanad, Kerala' },
      { id: '111', name: 'Idukki', coords: { lat: 9.8500, lng: 76.9700 }, address: 'Idukki, Kerala' },
      { id: '112', name: 'Pathanamthitta', coords: { lat: 9.2700, lng: 76.7800 }, address: 'Pathanamthitta, Kerala' },
      { id: '113', name: 'Kottayam', coords: { lat: 9.5833, lng: 76.5167 }, address: 'Kottayam, Kerala' },
      { id: '114', name: 'Ernakulam', coords: { lat: 9.9667, lng: 76.2833 }, address: 'Ernakulam, Kerala' },

      // Kerala - Hill Stations and Tourist Places
      { id: '115', name: 'Munnar', coords: { lat: 10.0889, lng: 77.0595 }, address: 'Munnar, Kerala' },
      { id: '116', name: 'Thekkady', coords: { lat: 9.5667, lng: 77.1667 }, address: 'Thekkady, Kerala' },
      { id: '117', name: 'Varkala', coords: { lat: 8.7333, lng: 76.7167 }, address: 'Varkala, Kerala' },
      { id: '118', name: 'Kumarakom', coords: { lat: 9.6167, lng: 76.4333 }, address: 'Kumarakom, Kerala' },
      { id: '119', name: 'Bekal', coords: { lat: 12.3833, lng: 75.0333 }, address: 'Bekal, Kerala' },

      // Major Airports
      { id: '120', name: 'Bangalore Airport', coords: { lat: 13.1986, lng: 77.7063 }, address: 'Devanahalli, Bangalore, Karnataka' },
      { id: '121', name: 'Hyderabad Airport', coords: { lat: 17.2403, lng: 78.4294 }, address: 'Shamshabad, Hyderabad, Andhra Pradesh' },
      { id: '122', name: 'Kochi Airport', coords: { lat: 10.1520, lng: 76.4019 }, address: 'Nedumbassery, Kochi, Kerala' },
      { id: '123', name: 'Trivandrum Airport', coords: { lat: 8.4821, lng: 76.9200 }, address: 'Thiruvananthapuram, Kerala' },
      { id: '124', name: 'Coimbatore Airport', coords: { lat: 11.0300, lng: 77.0434 }, address: 'Coimbatore, Tamil Nadu' },
      { id: '125', name: 'Madurai Airport', coords: { lat: 9.8344, lng: 78.0933 }, address: 'Madurai, Tamil Nadu' },
      { id: '126', name: 'Tiruchirapalli Airport', coords: { lat: 10.7654, lng: 78.7097 }, address: 'Tiruchirapalli, Tamil Nadu' },
      { id: '127', name: 'Salem Airport', coords: { lat: 11.7833, lng: 78.0667 }, address: 'Salem, Tamil Nadu' }
    ];

    return mockLocations.filter(location => 
      location.name.toLowerCase().includes(query.toLowerCase()) ||
      location.address.toLowerCase().includes(query.toLowerCase())
    );
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(e);
    
    if (newValue.length >= 3) {
      setShowSuggestions(true);
      debouncedSearch(newValue);
    } else {
      setShowSuggestions(false);
      setSuggestions([]);
    }
  };

  // Handle suggestion selection
  const handleSuggestionClick = async (suggestion) => {
    try {
      // Update the input field immediately
      onChange({ target: { name, value: suggestion.name } });
      setShowSuggestions(false);
      setSuggestions([]);

      // If coordinates are already available, use them
      if (suggestion.coords) {
        onLocationSelect(suggestion.name, suggestion.coords);
        return;
      }

      // Otherwise, geocode the address to get coordinates
      console.log(`[LocationSuggestions] Geocoding address: ${suggestion.address}`);

      // Try Google Maps geocoding first
      if (GoogleMapsService.isConfigured()) {
        try {
          const geocodeResult = await GoogleMapsService.geocodeAddress(suggestion.address);
          console.log(`[LocationSuggestions] Google Maps geocoding successful:`, geocodeResult.coords);
          onLocationSelect(suggestion.name, geocodeResult.coords);
          return;
        } catch (error) {
          console.error('[LocationSuggestions] Google Maps geocoding failed:', error);
        }
      }

      // Fallback to OpenRouteService geocoding
      try {
        const coords = await geocodeWithOpenRoute(suggestion.address);
        if (coords) {
          console.log(`[LocationSuggestions] OpenRouteService geocoding successful:`, coords);
          onLocationSelect(suggestion.name, coords);
          return;
        }
      } catch (error) {
        console.error('[LocationSuggestions] OpenRouteService geocoding failed:', error);
      }

      // If all geocoding fails, still select the location but without coordinates
      console.warn(`[LocationSuggestions] Could not geocode ${suggestion.address}, using without coordinates`);
      onLocationSelect(suggestion.name, null);

    } catch (error) {
      console.error('[LocationSuggestions] Error in handleSuggestionClick:', error);
      onLocationSelect(suggestion.name, null);
    }
  };

  // Handle input focus
  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clear input
  const handleClear = () => {
    onChange({ target: { name, value: '' } });
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  return (
    <div className="location-suggestions-container">
      <div className="input-group">
        <input
          ref={inputRef}
          type="text"
          name={name}
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          className={`form-input ${error ? 'error' : ''}`}
          autoComplete="off"
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="clear-button"
            aria-label="Clear input"
          >
            <FaTimes />
          </button>
        )}
        {isLoading && (
          <div className="loading-spinner-small"></div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div ref={suggestionsRef} className="suggestions-dropdown">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.id}
              className="suggestion-item"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <FaMapMarkerAlt className="suggestion-icon" />
              <div className="suggestion-content">
                <div className="suggestion-name">{suggestion.name}</div>
                <div className="suggestion-address">{suggestion.address}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showSuggestions && suggestions.length === 0 && !isLoading && value.length >= 3 && (
        <div ref={suggestionsRef} className="suggestions-dropdown">
          <div className="no-suggestions">
            <FaSearch className="no-suggestions-icon" />
            <span>No locations found</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSuggestions;